
import { FC, useState } from "react";
import { Domain } from "@/types/domain";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { FileText, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

interface DomainElementsProps {
  domains: Domain[];
}

const getGsaKnowledgeBases = (symbol: string, t: (key: string) => string) => {
  switch(symbol) {
    case "Clean Energy & Grid Infrastructure":
      return [
        { title: t('kb.quebecHydroTransmission') || "Québec Hydroelectric Transmission Models", url: "#" },
        { title: t('kb.champlainHudsonExport') || "Export Case Study: Champlain Hudson Power Express", url: "#" },
        { title: t('kb.boralexInnergexRole') || "Role of Boralex and Innergex in U.S. Grid Decarbonization", url: "#" },
        { title: t('kb.batteryStoragePartnerships') || "Battery Storage Partnerships with Southern U.S. Utilities", url: "#" },
        { title: t('kb.quebecCapTradeSystem') || "Quebec's Cap-and-Trade System Linked with California", url: "#" }
      ];
    case "Electric & Sustainable Transportation":
      return [
        { title: t('kb.lionElectricBus') || "Lion Electric: Quebec's Electric Bus Manufacturing Hub", url: "#" },
        { title: t('kb.quebecBatteryTesla') || "Quebec Battery Materials Supply Chain to Tesla", url: "#" },
        { title: t('kb.evChargingPartnerships') || "Cross-border EV Charging Infrastructure Partnerships", url: "#" },
        { title: t('kb.lithiumMiningQuebec') || "Lithium Mining and Processing in Nord-du-Québec", url: "#" },
        { title: t('kb.montrealMetroElectrification') || "Montreal Metro Electrification Export Models", url: "#" }
      ];
    case "Aerospace & Advanced Aviation":
      return [
        { title: t('kb.bombardierAirbus') || "Bombardier-Airbus Partnership in Mobile, Alabama", url: "#" },
        { title: t('kb.prattWhineyCanada') || "Pratt & Whitney Canada Engine Manufacturing", url: "#" },
        { title: t('kb.bellHelicopterQuebec') || "Bell Helicopter Textron Quebec Operations", url: "#" },
        { title: t('kb.caeFlightSimulation') || "CAE Flight Simulation Training Centers in U.S.", url: "#" },
        { title: t('kb.quebecAerospaceCluster') || "Quebec Aerospace Cluster Export Strategies", url: "#" }
      ];
    default:
      const domainMap: Record<string, string> = {
        "Clean Energy & Grid Infrastructure": "domain.cleanEnergyGrid",
        "Electric & Sustainable Transportation": "domain.electricTransport", 
        "Aerospace & Advanced Aviation": "domain.aerospace",
        "Critical & Strategic Minerals": "domain.criticalMinerals",
        "AI, ICT, and Smart Industry": "domain.aiIct",
        "Pulp, Paper & Forestry Innovation": "domain.forestry",
        "Aluminum & Industrial Manufacturing": "domain.aluminum",
        "Life Sciences & Biomedical Supply Chain": "domain.lifeSciences",
        "Agri-Food & Bioeconomy": "domain.agriFood",
        "Green Economy & Climate Diplomacy": "domain.greenEconomy",
        "Logistics, Ports, and Maritime Trade": "domain.logistics",
        "Public Transit & Urban Mobility": "domain.publicTransit",
        "Academic & Research Collaboration": "domain.academic",
        "Tourism, Culture & Francophonie": "domain.tourism",
        "Public Procurement & Economic Diplomacy": "domain.procurement"
      };
      const translatedDomain = t(domainMap[symbol] || symbol);
      return [
        { title: `${translatedDomain} Knowledge Base 1`, url: "#" },
        { title: `${translatedDomain} Knowledge Base 2`, url: "#" },
        { title: `${translatedDomain} Knowledge Base 3`, url: "#" }
      ];
  }
};

const DomainElements: FC<DomainElementsProps> = ({ domains }) => {
  const [expandedDomain, setExpandedDomain] = useState<string | null>(null);
  const { t } = useTranslation();

  const getDomainTranslationKey = (symbol: string) => {
    const domainMap: Record<string, string> = {
      "Clean Energy & Grid Infrastructure": "domain.cleanEnergyGrid",
      "Electric & Sustainable Transportation": "domain.electricTransport", 
      "Aerospace & Advanced Aviation": "domain.aerospace",
      "Critical & Strategic Minerals": "domain.criticalMinerals",
      "AI, ICT, and Smart Industry": "domain.aiIct",
      "Pulp, Paper & Forestry Innovation": "domain.forestry",
      "Aluminum & Industrial Manufacturing": "domain.aluminum",
      "Life Sciences & Biomedical Supply Chain": "domain.lifeSciences",
      "Agri-Food & Bioeconomy": "domain.agriFood",
      "Green Economy & Climate Diplomacy": "domain.greenEconomy",
      "Logistics, Ports, and Maritime Trade": "domain.logistics",
      "Public Transit & Urban Mobility": "domain.publicTransit",
      "Academic & Research Collaboration": "domain.academic",
      "Tourism, Culture & Francophonie": "domain.tourism",
      "Public Procurement & Economic Diplomacy": "domain.procurement"
    };
    return domainMap[symbol] || symbol;
  };

  const handleDomainClick = (domain: Domain) => {
    if (expandedDomain === domain.symbol) {
      setExpandedDomain(null);
    } else {
      setExpandedDomain(domain.symbol);
    }
  };

  const handleKnowledgeBaseClick = (url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(url, "_blank");
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
      {domains.map((domain) => (
        <div key={domain.symbol} className="flex flex-col">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div 
                  className="bg-blue-600 text-white p-4 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:bg-black hover:shadow-lg flex flex-col items-center relative h-24 flex items-center justify-center"
                  onClick={() => handleDomainClick(domain)}
                >
                  <div className="text-xs font-bold text-center mb-1">{t(getDomainTranslationKey(domain.symbol)) || domain.symbol}</div>
                  
                  <div className="absolute right-2 top-2">
                    {expandedDomain === domain.symbol ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent 
                side="right"
                className="bg-white text-black border border-gray-200 max-w-[400px] whitespace-pre-line"
              >
                <p className="font-semibold mb-2">{t(getDomainTranslationKey(domain.symbol)) || domain.name}</p>
                <p className="text-sm">{t('domain.representativeKnowledgeBases')}</p>
                <p className="text-xs mt-1">{domain.categories}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {expandedDomain === domain.symbol && (
            <div className="mt-2 bg-white rounded-lg shadow-md p-3 border border-gray-200 z-10">
              <h3 className="text-sm font-semibold mb-2">{t('domain.knowledgeBases')}</h3>
              <div className="space-y-2">
                {getGsaKnowledgeBases(domain.symbol, t).map((kb, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-2 p-2 rounded text-xs"
                  >
                    <FileText className="h-3 w-3 text-blue-600 flex-shrink-0" />
                    <span className="flex-1">{kb.title}</span>
                    <ExternalLink className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DomainElements;
